<template>
    <div class="p-4">
        <!-- Channel -->
        <div>
            <h5>Channel</h5>
            <div class="form-group" :class="{ error: $v.refundObject.refundChannels.$error }">
                <channel-selector v-model="refundObject.refundChannels" @input="channelChanged" :reservation="getSelectedReservation" />

                <div v-if="$v.refundObject.refundChannels.$error" class="messages">
                    <span v-if="!$v.refundObject.refundChannels.required">Select one.</span>
                </div>
            </div>
        </div>

        <!-- Scope summary -->
        <div class="mt-1 mb-2">
            <payment-row
                v-if="selectedScope === REFUND_SCOPE.SELECT_PAYMENT && selectedPayment"
                :payment="selectedPayment"
                :reservation="getSelectedReservation"
                :plain="true"
            />
            <div v-else class="refund__all-summary">
                All payments — {{ formatPrice(scopeTotal) }} €
            </div>
        </div>

        <!-- When to refund -->
        <div class="mt-3">
            <h5>When to refund</h5>
            <div class="form-group" :class="{ error: $v.refundObject.isRequest.$error, 'opacity-50': disableRefundRequestArea }">
                <div class="whenToRefund radius-normal">
                    <div class="whenToRefund__option d-flex align-items-center p-2 pointer" @click="clickRefundRequestType(false)">
                        <div class="mx-2">
                            <radio name="isRequest" :disabled="disableRefundRequestArea" :value="false" v-model="refundObject.isRequest" @input="$v.refundObject.isRequest.$touch"></radio>
                        </div>
                        <div class="darker">Refund customer now</div>
                    </div>

                    <div class="whenToRefund__option d-flex align-items-center p-2 pointer" @click="clickRefundRequestType(true)">
                        <div class="mx-2">
                            <radio name="isRequest" :disabled="disableRefundRequestArea" :value="true" v-model="refundObject.isRequest" @input="$v.refundObject.isRequest.$touch"></radio>
                        </div>
                        <div class="darker">Request refund</div>
                    </div>
                </div>

                <div v-if="$v.refundObject.isRequest.$error" class="messages">
                    <span v-if="!$v.refundObject.isRequest.required">Select one.</span>
                </div>
            </div>
        </div>

        <!-- Payment Method -->
        <div class="mt-3">
            <h5>Payment Method</h5>
            <div class="form-group" :class="{ error: $v.refundObject.paymentType.$error }">
                <payment-method-selector :allowedMethods="allowedPaymentMethods" @change="paymentMethodPickerChange" v-model="refundObject.paymentType" />

                <div v-if="$v.refundObject.paymentType.$error" class="messages">
                    <span v-if="!$v.refundObject.paymentType.required">Select one.</span>
                </div>
            </div>
        </div>

        <!-- Refund Type -->
        <div class="mt-3">
            <h5>Refund type</h5>
            <div class="form-group" :class="{ error: $v.selectedRefundType.$error }">
                <div class="d-flex flex-wrap">
                    <button
                        type="button"
                        v-for="refundType in getRefundTypes"
                        :key="refundType.id"
                        v-tooltip="refundTypeDisabledReason(refundType)"
                        class="refundTypeBtn btn bg-lightest darker bold mr-2 mb-2"
                        :class="{ 'selected': isRefundTypeSelected(refundType), 'disabled': disableRefundTypeArea || isRefundTypeDisabled(refundType) }"
                        @click="selectRefundType(refundType)"
                        :disabled="isRefundTypeDisabled(refundType)">
                        {{ refundType.label }}
                    </button>
                </div>

                <div v-if="$v.selectedRefundType.$error" class="messages">
                    <span v-if="!$v.selectedRefundType.required">Select one.</span>
                </div>
            </div>
        </div>

        <!-- Sub option for refund type -->
        <div v-if="selectedRefundType != null">
            <!-- Percentage or Value -->
            <div v-if="selectedRefundType.id == REFUND_TYPE.PERCENTAGE_OR_VALUE" class="mt-3">
                <h5>Percentage or Value</h5>
                <div class="form-group" :class="{ error: $v.selectedRefundType.$error }">
                    <div class="percentValue col-sm-12 d-flex align-items-center">
                        <div class="percentValue__label bold mr-2">Value:</div>

                        <div class="d-flex flex-column">
                            <div class="d-flex">
                                <input
                                    type="number"
                                    class="percentValue__input glued-right"
                                    v-model.number="refundObject.amount"
                                    :max="isEuroMode ? refundMaximumValue : 100"
                                    :step="isEuroMode ? 0.01 : 1"
                                    :min="0"
                                    @input="onAmountInput"
                                    @keydown="preventDecimalInput"
                                    v-numFloat
                                >
                                <v-select class="percentValue__select glued-left" :options="getPercentageOrCurrency" v-model="selectedValueType" @input="valueTypeChanged($event)"></v-select>
                            </div>
                            <div v-if="isEuroMode && refundMaximumValue != null" class="fs-smaller darker mt-1">
                                Max: {{ formatPrice(refundMaximumValue) }} €
                            </div>
                        </div>
                    </div>

                    <div v-if="$v.selectedRefundType.$error" class="messages">
                        <span v-if="!$v.selectedRefundType.isValid">
                            <span v-if="isEuroMode && refundMaximumValue != null">Value must be between 0 and {{ formatPrice(refundMaximumValue) }} €.</span>
                            <span v-else>Invalid value.</span>
                        </span>
                    </div>
                </div>
            </div>

            <!-- Reduce Quantity (Pax and Extras) -->
            <div v-if="selectedRefundType.id == REFUND_TYPE.ITEM_VALUE" class="mt-3">
                <h5>Reservation Items</h5>
                <div class="form-group" :class="{ error: $v.refundItems.$error }">
                    <div class="col-sm-12">
                        <div class="w-100 text-center py-4" v-if="calculationResult == null">
                            <loader />
                        </div>
                        <div v-else>
                            <reduce-quantity v-model="refundObject.refundItems" v-if="calculationResult != null" :calculations="calculationResult" :compact="true" @change="requestCalculations()" />

                            <div class="releaseQty d-flex align-items-center p-2 radius-normal bg-lighter pointer" :class="{ 'releaseQty--disabled': releaseQuantityBlocked }" @click="toggleReleaseQuantity">
                                <div class="mx-2">
                                    <checkbox v-model="refundObject.isToReleaseQuantity" :disabled="releaseQuantityBlocked"></checkbox>
                                </div>
                                <div class="darker">
                                    <div class="releaseQty__title bold">Make refunded items available again</div>
                                    <div class="releaseQty__subtitle">This will allow to re-sell refunded items</div>
                                    <div v-if="releaseQuantityBlocked" class="releaseQty__warning">
                                        The reservation must keep at least one passenger.
                                    </div>
                                </div>
                            </div>

                            <div v-if="$v.refundItems.$error" class="messages">
                                <span v-if="!$v.refundItems.isValid">Select at least one item to refund.</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Reason -->
        <div class="mt-3">
            <h5>Reason <span class="color-6">*</span></h5>
            <div class="form-group" :class="{ error: $v.refundObject.refundReasonType.$error }">
                <v-select :options="getRefundReasons" :disabled="disableReasonArea" label="text" placeholder="Select reason" v-model="selectedReason" @input="reasonChanged"></v-select>

                <div v-if="refundObject.refundReasonType == 0" class="mt-3">
                    <label>Describe the refund reason</label>
                    <input type="text" class="w-100" v-model="refundObject.reason">
                </div>

                <div v-if="$v.refundObject.refundReasonType.$error" class="messages">
                    <span v-if="!$v.refundObject.refundReasonType.required">Reason is required.</span>
                </div>
            </div>
        </div>

        <!-- Value to Refund -->
         
        <div class="mt-3">
            <div class="bold dark text-right">
                <loader v-if="isLoadingCalculations" />
                <div v-else class="d-flex flex-column">
                    <div v-if="refundValueProcessed.now != null" class="d-flex flex-grow-1 justify-content-end">
                        <span>Refund now: <span class="color-1">{{ formatPrice(refundValueProcessed.now) }} €</span></span>
                    </div>
                    <div v-if="refundValueProcessed.later != null" class="d-flex flex-grow-1 justify-content-end">
                        <span>Refund later: <span class="color-1">{{ formatPrice(refundValueProcessed.later) }} €</span></span>
                    </div>
                    <div v-if="refundMaximumValue != null && refundValue > refundMaximumValue" class="messages d-flex flex-grow-1 justify-content-end mt-1">
                        <span>Exceeds maximum refundable amount of {{ formatPrice(refundMaximumValue) }} €.</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapGetters, mapActions } from 'vuex'
    import { required } from 'vuelidate/lib/validators'
    import _ from 'lodash'
    import Vue from 'vue'

    import paymentMethodSelector from '@/tools/modal/ReservationRefundTransaction/PaymentMethodSelector'
    import extendedVSelect from '@/extends/vue-select'
    import Loader from '@/tools/loaders/loader1'
    import channelSelector from '@/tools/modal/ReservationRefundTransaction/ChannelSelector'
    import reduceQuantity from '@/components/reservations/refund/ReduceQuantity'
    import PaymentRow from '@/tools/modal/ReservationRefundTransaction/PaymentRow'
    import { REFUND_SCOPE, REFUND_TYPE, PAYMENT_STATUS_PARTIALLY_PAID } from '@/utils/refundHelpers'

    import { reservationsMixin } from '@/mixins/reservations'
    import { dialogMixin } from '@/mixins/dialog'
    import refundService from '@/api/reservation/refundService'

    export default {
        name: 'RefundStep2Details',
        mixins: [ reservationsMixin, dialogMixin ],
        props: {
            // Scope chosen in step 1, used only for the scope summary line.
            selectedScope: {
                type: String,
                default: null,
            },
            selectedPayment: {
                type: Object,
                default: null,
            },
            scopeTotal: {
                type: Number,
                default: 0,
            },
        },
        created() {
            this.debouncedValueChanged = _.debounce(this.valueChanged, 500);
            // Select percent
            this.selectedValueType = this.getPercentageOrCurrency[0];

            // Start with a clean refund object. The scope (payment id / transaction code) was
            // already committed to the store by the parent before this step mounted.
            this.$set(this, 'refundObject', JSON.parse(JSON.stringify(this.refundObjectClean)));
            this.refundObject.userAccount = this.account;

            this.initRefundMaximumValue();
        },
        mounted() {
            // Preselected - Make refunded items available again
            this.refundObject.isToReleaseQuantity = true;
        },
        data() {
            return {
                REFUND_SCOPE,
                REFUND_TYPE,
                selectedRefundType: null,
                selectedValueType: null,
                selectedReason: null,
                refundObjectClean: {
                    refundChannels: [],
                    reason: null,
                    refundReasonType: null,
                    amount: 0,
                    amountType: 1, // Start with percentage (1)
                    isToReleaseQuantity: false,
                    isToRefundAtSource: false,
                    isToRefundTotal: false,
                    isRequest: null,
                    paymentType: null,
                    userAccount: null,
                    refundItems: []
                },
                refundObject: null,
                isLoadingCalculations: false,
                isSubmitting: false,
                refundValue: 0,
                refundMaximumValue: null,
                calculationResult: null,
                debouncedValueChanged: null
            }
        },
        validations: {
            selectedRefundType: {
                required,
                isValid: (val, vm) => {
                    if (val == null) return false;

                    const exceedsMax = vm.refundMaximumValue != null && vm.refundValue > vm.refundMaximumValue;
                    if (exceedsMax) return false;

                    if (val.id !== REFUND_TYPE.PERCENTAGE_OR_VALUE) return true;

                    // Partial - Percentage
                    if (vm.refundObject.amountType === 1) {
                        return Number(vm.refundObject.amount) > 0 && Number(vm.refundObject.amount) <= 100;
                    }

                    // Partial - Euro: check directly against max for immediate feedback
                    if (vm.refundMaximumValue == null) {
                        return Number(vm.refundObject.amount) > 0;
                    }
                    return Number(vm.refundObject.amount) > 0 && Number(vm.refundObject.amount) <= vm.refundMaximumValue;
                }
            },
            refundObject: {
                refundChannels: {
                    required,
                },
                refundReasonType: {
                    required,
                },
                paymentType: {
                    required,
                },
                isRequest: {
                    required: (val) => {
                        return val != null;
                    }
                },
            },
            refundItems: {
                isValid: (val, vm) => {
                    if (vm.selectedRefundType == null) {
                        return true
                    }

                    if (vm.selectedRefundType.id != 2) {
                        return true;
                    } else {
                        if (vm.refundObject.refundItems && vm.refundObject.refundItems.length > 0) {
                            return vm.refundObject.refundItems.filter(refundItem => refundItem.quantity > 0).length > 0;
                        } else {
                            return false;
                        }
                    }
                }
            }
        },
        computed: {
            ...mapGetters([
                'getSelectedReservation',
                'getRefundTypes',
                'getPercentageOrCurrency',
                'account',
                'getRefundReasons',
                'getPaymentTypeListAll',
                'getRefundPaymentId',
                'getRefundTransactionCode',
            ]),
            isPaymentScoped() {
                return this.getRefundPaymentId != null || this.getRefundTransactionCode != null;
            },
            scopedPayment() {
                const payments = this.getSelectedReservation.payments || [];

                if (this.getRefundTransactionCode != null) {
                    // Exclude fully-refunded payments (refundMaximumValue === 0): their items
                    // are already consumed and must not reappear as available to refund. This
                    // matters when a payment was totally refunded — its refundItems come back
                    // empty, so there is no item-level record to subtract, but the payment's
                    // own refundMaximumValue already marks it as fully refunded.
                    const group = this.transactionPaymentsInScope;
                    return group.length > 0 ? this.mergeTransactionPayments(group) : null;
                }

                if (this.getRefundPaymentId != null) {
                    return payments.find(p => p.id === this.getRefundPaymentId) || null;
                }

                return null;
            },
            // Refundable payments grouped under the transaction being refunded
            transactionPaymentsInScope() {
                if (this.getRefundTransactionCode == null) {
                    return [];
                }
                const payments = this.getSelectedReservation.payments || [];
                return payments
                    .filter(p => p.transactionCode === this.getRefundTransactionCode)
                    .filter(p => p.refundMaximumValue !== 0);
            },
            // True when the refund effectively targets one payment: either a specific payment
            // was picked, or the transaction being refunded has a single refundable payment.
            isSinglePaymentRefund() {
                if (this.getRefundPaymentId != null) {
                    return true;
                }
                return this.transactionPaymentsInScope.length === 1;
            },
            // True when the same item (a ticket or component) is split across more than one
            // payment of the transaction — in that case its allocation per payment is ambiguous.
            hasDuplicateItemsAcrossPayments() {
                const seenItems = new Set();

                for (const payment of this.transactionPaymentsInScope) {
                    const paymentItems = new Set();
                    (payment.tickets || []).forEach(ticket => paymentItems.add(`ticket-${ticket.itemId}`));
                    (payment.components || []).forEach(component => paymentItems.add(`component-${component.itemId}`));

                    for (const itemKey of paymentItems) {
                        if (seenItems.has(itemKey)) {
                            return true;
                        }
                        seenItems.add(itemKey);
                    }
                }

                return false;
            },
            // Refunds that belong to the payment/transaction currently in scope. Each payment
            // carries its own `refunds` array, so a payment-scoped refund only looks at that
            // payment, and a transaction-scoped one at every payment sharing the transaction
            // code. An unscoped (all payments) refund falls back to every refund on the
            // reservation.
            scopedRefunds() {
                const payments = this.getSelectedReservation.payments || [];

                if (this.getRefundTransactionCode != null) {
                    return payments
                        .filter(p => p.transactionCode === this.getRefundTransactionCode)
                        .flatMap(p => p.refunds || []);
                }

                if (this.getRefundPaymentId != null) {
                    const payment = payments.find(p => p.id === this.getRefundPaymentId);
                    return payment ? (payment.refunds || []) : [];
                }

                return [
                    ...(this.getSelectedReservation.generalRefunds || []),
                    ...(this.getSelectedReservation.refunds || []),
                ];
            },
            // True when a percentage/value refund (not total, no item breakdown) was already
            // made against the payment/transaction in scope. Such a refund reduces the
            // refundable amount by an arbitrary value, so the remainder no longer maps to
            // whole items and item-value refunds must no longer be offered for that scope.
            hasPercentageOrValueRefund() {
                return this.scopedRefunds.some(refund => {
                    // Skip the in-progress refund being edited (no real id yet).
                    if (!refund || !refund.id) {
                        return false;
                    }

                    const isTotal = refund.isToRefundTotal === true;
                    const hasItems =
                        (Array.isArray(refund.refundItems) && refund.refundItems.length > 0) ||
                        (Array.isArray(refund.refundDetailsItems) && refund.refundDetailsItems.length > 0);

                    return !isTotal && !hasItems;
                });
            },
            // True when any payment in the refund scope (the selected payment, or any
            // refundable payment of the selected transaction) is only partially paid.
            // Such a payment doesn't cover its items' full value, so an item-value
            // refund can't map whole items back to what was actually paid.
            scopeHasPartiallyPaidPayment() {
                if (this.getRefundTransactionCode != null) {
                    return this.transactionPaymentsInScope.some(p => p.paymentStatus === PAYMENT_STATUS_PARTIALLY_PAID);
                }

                const payment = this.scopedPayment;
                return payment != null && payment.paymentStatus === PAYMENT_STATUS_PARTIALLY_PAID;
            },
            disableRefundRequestArea() {
                return !this.refundObject.refundChannels || this.refundObject.refundChannels.length < 1;
            },
            disableRefundTypeArea() {
                return this.refundObject.paymentType == null;
            },
            onlyTotalAvailable() {
                return this.getRefundTypes.every(refundType =>
                    refundType.id === REFUND_TYPE.TOTAL || this.isRefundTypeDisabled(refundType)
                );
            },
            disableReasonArea() {
                return this.selectedRefundType == null;
            },
            refundValueProcessed() {
                if (this.refundObject.isRequest) {
                    return {
                        now: 0,
                        later: this.refundValue
                    }
                } else {
                    return {
                        now: this.refundValue,
                        later: null
                    }
                }
            },
            allowedPaymentMethods() {
                // ALLOW ALL FOR ALL
                if (this.refundObject.refundChannels.length > 0 && this.refundObject.isRequest != null) {
                    return this.getPaymentTypeListAll.map(item => item.id);
                } else {
                    return [];
                }
            },
            exceedsMaximumValue() {
                return this.refundMaximumValue != null && this.refundValue > this.refundMaximumValue;
            },
            isRefundValid() {
                if (this.exceedsMaximumValue || this.refundValue <= 0) return false;
                return this.$v.selectedRefundType.$invalid == false && this.$v.refundObject.$invalid == false && this.$v.refundItems.$invalid == false;
            },
            soldPax() {
                if (this.refundObject.refundChannels.length > 0) {
                    return this.getSelectedReservation.bookingGenderPrices.filter(soldItem => soldItem.channelType == this.refundObject.refundChannels[0].channelType);
                } else {
                    return this.getSelectedReservation.bookingGenderPrices;
                }
            },
            soldExtras() {
                if (this.refundObject.refundChannels.length > 0) {
                    return this.getSelectedReservation.bookingComponents.filter(soldItem => soldItem.channelType == this.refundObject.refundChannels[0].channelType);
                } else {
                    return this.getSelectedReservation.bookingComponents;
                }
            },
            isEuroMode() {
                return this.refundObject && this.refundObject.amountType === 0;
            },
            // Passengers currently booked on the reservation (all channels).
            bookedPaxQuantity() {
                return (this.getSelectedReservation.bookingGenderPrices || [])
                    .reduce((sum, pax) => sum + (pax.quantityToBook || 0), 0);
            },
            // Passengers selected for refund in the current item-value refund.
            refundedPaxQuantity() {
                return ((this.refundObject && this.refundObject.refundItems) || [])
                    .filter(item => item.bookingGenderPrice)
                    .reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
            },
            // Releasing the refunded items would leave the reservation active with
            // zero passengers, which is not allowed.
            releaseQuantityBlocked() {
                return this.bookedPaxQuantity > 0 && this.refundedPaxQuantity >= this.bookedPaxQuantity;
            },
            emailWarningMessage() {
                return `Customer ${this.refundObject && this.refundObject.refundChannels && this.refundObject.refundChannels[0] && this.refundObject.refundChannels[0].channelType ? 'and Agent' : ''} will receive an email with refund information.`;
            }
        },
        watch: {
            // Keep the wizard footer (owned by the parent) in sync with this step's state.
            isRefundValid: {
                immediate: true,
                handler(val) { this.$emit('update:valid', val); }
            },
            isSubmitting: {
                immediate: true,
                handler(val) { this.$emit('update:submitting', val); }
            },
            emailWarningMessage: {
                immediate: true,
                handler(val) { this.$emit('update:email-warning', val); }
            },
            releaseQuantityBlocked(blocked) {
                if (blocked) {
                    this.refundObject.isToReleaseQuantity = false;
                }
            }
        },
        methods: {
            ...mapActions([
                'setReservationCalculations',
                'refreshCurrentSelectedReservation',
                'closeModal',
            ]),
            isRefundTypeDisabled(refundType) {
                // Total is always available.
                if (refundType.id === REFUND_TYPE.TOTAL) {
                    return false;
                }

                // Once a percentage/value (general) refund exists, the remaining amount no
                // longer maps to whole items, so item-value refunds are no longer available
                // regardless of scope.
                if (refundType.id === REFUND_TYPE.ITEM_VALUE && this.hasPercentageOrValueRefund) {
                    return true;
                }

                // A partially paid payment in the scope doesn't cover its items' full
                // value, so item-value refunds can't be offered.
                if (refundType.id === REFUND_TYPE.ITEM_VALUE && this.scopeHasPartiallyPaidPayment) {
                    return true;
                }

                // A single-payment refund supports every refund type.
                if (this.isSinglePaymentRefund) {
                    return false;
                }

                // Multi-payment transaction refund:
                // - Percentage or Value is is never available.
                if (refundType.id === REFUND_TYPE.PERCENTAGE_OR_VALUE) {
                    return true;
                }
                // - Item Value is available only when no item is split across payments.
                if (refundType.id === REFUND_TYPE.ITEM_VALUE) {
                    return this.hasDuplicateItemsAcrossPayments;
                }

                return false;
            },
            refundTypeDisabledReason(refundType) {
                if (this.isRefundTypeDisabled(refundType)) {
                    if (refundType.id === REFUND_TYPE.ITEM_VALUE) {
                        if (this.hasPercentageOrValueRefund) {
                            return 'Item value refunds are no longer available because a percentage or value refund was already made.';
                        }
                        if (this.scopeHasPartiallyPaidPayment) {
                            return 'Item value refunds are not available because a payment is only partially paid.';
                        }
                        return 'Only available when each item is unique across all payments.';
                    }
                    return 'Only available when selecting a specific payment.';
                }

                return null;
            },
            channelChanged() {
                // Clear refund type | Clear payment method
                this.refundObject.paymentType = null;

                if (this.selectedRefundType != null) {
                    this.selectedRefundType = null;
                    this.updateAvailableRefundItems();
                }
            },
            initRefundMaximumValue() {
                if (this.isPaymentScoped) {
                    const payment = this.scopedPayment;
                    this.refundMaximumValue = (payment && payment.refundMaximumValue != null)
                        ? payment.refundMaximumValue
                        : null;
                } else {
                    this.refundMaximumValue = this.getSelectedReservation.globalRefundMaximumValue;
                }
            },
            // Aggregates the payments of a transaction (sharing a transaction code) into a
            // single payment-like object, summing refundable values and concatenating the
            // tickets/components so a transaction refund behaves like a single-payment one.
            mergeTransactionPayments(group) {
                const primary = group[0];
                const sumOrNull = (key) => {
                    const values = group.map(p => p[key]).filter(v => v != null);
                    return values.length > 0 ? values.reduce((sum, v) => sum + v, 0) : null;
                };

                return {
                    ...primary,
                    refundMaximumValue: sumOrNull('refundMaximumValue'),
                    tickets: group.flatMap(p => p.tickets || []),
                    components: group.flatMap(p => p.components || []),
                };
            },
            createRefundItemsArray() {
                let output = [];
                let cleanPax = [];
                let cleanExtras = [];

                const payment = this.scopedPayment;

                // PAX
                if (this.soldPax && this.soldPax.length > 0) {
                    cleanPax = JSON.parse(JSON.stringify(this.soldPax));
                    cleanPax = cleanPax.filter(pax => { return pax.quantityToBook > 0 });

                    cleanPax = cleanPax.map(pax => {
                        let paymentTicketQuantity;

                        if (payment && payment.tickets && payment.tickets.length > 0) {
                            const matchingTickets = payment.tickets.filter(ticket => ticket.itemId === pax.id);
                            if (matchingTickets.length > 0) {
                                paymentTicketQuantity = matchingTickets.reduce((sum, t) => sum + (t.quantity || 0), 0);
                            }
                        }
                        if (payment) {
                            pax.quantityToBook = paymentTicketQuantity || 0;
                        }
                        return {
                            refundType: null,
                            quantity: 0,
                            bookingGenderPrice: pax
                        }
                    }).filter(item => item.bookingGenderPrice.quantityToBook > 0);
                }

                // EXTRAS
                if (this.soldExtras && this.soldExtras.length > 0) {
                    cleanExtras = JSON.parse(JSON.stringify(this.soldExtras));

                    cleanExtras = cleanExtras.map(extra => {
                        let paymentTicketQuantity;

                        if (payment && payment.components && payment.components.length > 0) {
                            const matchingComponents = payment.components.filter(component => component.itemId === extra.id);
                            if (matchingComponents.length > 0) {
                                paymentTicketQuantity = matchingComponents.reduce((sum, c) => sum + (c.quantity || 0), 0);
                            }
                        }
                        if (payment) {
                            extra.quantity = paymentTicketQuantity || 0;
                        }
                        return {
                            refundType: null,
                            quantity: 0,
                            bookingComponent: extra
                        }
                    }).filter(item => item.bookingComponent.quantity > 0);
                }

                output = output.concat(cleanPax, cleanExtras);

                return output;
            },
            paymentMethodPickerChange(e) {
                if (e == null) {
                    this.refundObject.paymentType = null;
                }
                this.$v.refundObject.paymentType.$touch;
                this.preselectTotalWhenOnlyOption();
            },
            preselectTotalWhenOnlyOption() {
                if (this.disableRefundTypeArea || !this.onlyTotalAvailable || this.selectedRefundType != null) {
                    return;
                }

                const totalRefundType = this.getRefundTypes.find(refundType => refundType.id === REFUND_TYPE.TOTAL);
                if (totalRefundType) {
                    this.selectRefundType(totalRefundType);
                }
            },
            valueTypeChanged(e) {
                this.refundObject.amountType = e.id;
                this.updateAmountResumeValue();
                this.requestCalculations();
            },
            valueChanged() {
                this.updateAmountResumeValue();
                this.requestCalculations();
            },
            updateAmountResumeValue() {
                const newValue = this.refundObject.amount !== 0 ? 0 : this.total;
                this.$set(this.refundObject, 'amountResumeValue', newValue);
            },
            updateAvailableRefundItems() {
                this.refundObject.refundItems = this.createRefundItemsArray();
                this.requestCalculations();
            },
            selectRefundType(refundType) {
                if (this.disableRefundTypeArea || this.isRefundTypeDisabled(refundType)) {
                    return false;
                }

                this.selectedRefundType = refundType;

                const isTotalOrPartial = refundType.id === REFUND_TYPE.TOTAL || refundType.id === REFUND_TYPE.PERCENTAGE_OR_VALUE;

                if (isTotalOrPartial) {
                    this.$set(this.refundObject, 'refundItems', []);
                    this.refundObject.isToRefundTotal = refundType.id === REFUND_TYPE.TOTAL;
                    this.refundObject.amount = refundType.id === REFUND_TYPE.TOTAL ? null : 0;
                    this.updateAmountResumeValue();
                    this.requestCalculations();
                } else if (refundType.id === REFUND_TYPE.ITEM_VALUE) {
                    this.updateAvailableRefundItems();
                    this.refundObject.amount = null;
                    this.refundObject.isToRefundTotal = false;
                }
            },
            isRefundTypeSelected(refundType) {
                return this.selectedRefundType != null && this.selectedRefundType.id == refundType.id;
            },
            clickRefundRequestType(e) {
                if (this.disableRefundRequestArea) {
                    return false;
                }

                // Reset downstream settings
                this.selectedRefundType = null;
                this.refundObject.paymentType = null;

                this.refundObject.isRequest = e;
                this.$set(this.refundObject, 'paymentType', null);
                this.$v.refundObject.isRequest.$touch;
            },
            toggleReleaseQuantity() {
                if (this.releaseQuantityBlocked) {
                    return;
                }

                this.refundObject.isToReleaseQuantity = !this.refundObject.isToReleaseQuantity;
            },
            reasonChanged(e) {
                this.refundObject.refundReasonType = e.id;
            },
            addRefundToReservation(refund, targetReservation) {
                let foundEditingRefund = targetReservation.refunds.find(reservationRefund => {
                    return typeof reservationRefund.id == 'undefined' || reservationRefund.id == 0
                });

                if (foundEditingRefund) {
                    foundEditingRefund = refund;
                } else {
                    targetReservation.refunds.push(refund);
                }
            },
            calculateRefundValueLocally() {
                let value = 0;

                if (this.selectedRefundType && this.selectedRefundType.id === REFUND_TYPE.TOTAL) {
                    value = this.refundMaximumValue || 0;
                } else if (this.selectedRefundType && this.selectedRefundType.id === REFUND_TYPE.PERCENTAGE_OR_VALUE) {
                    if (this.refundObject.amountType === 1) {
                        // Percentage
                        value = this.refundMaximumValue != null
                            ? Math.floor(this.refundMaximumValue * Number(this.refundObject.amount)) / 100
                            : 0;
                    } else {
                        // Euro value
                        value = Number(this.refundObject.amount) || 0;
                    }
                }

                this.refundValue = value;
                this.refundObject.amountResumeValue = value;
            },
            requestCalculations() {
                // For payment/transaction refunds with total or percentage/value, calculate locally — the
                // global reservation calculations endpoint doesn't account for payment-scoped amounts.
                if (this.isPaymentScoped && (!this.selectedRefundType || this.selectedRefundType.id !== REFUND_TYPE.ITEM_VALUE)) {
                    this.calculateRefundValueLocally();
                    return;
                }

                this.isLoadingCalculations = true;
                let tempReservation = JSON.parse(JSON.stringify(this.getSelectedReservation));

                this.addRefundToReservation(this.refundObject, tempReservation);
                this.setReservationCalculations({
                    reservation: tempReservation,
                    onlyCalculate: true
                }).then(response => {
                    if (response && response.data) {
                        this.calculationResult = response.data;
                        if (response.data.refund && response.data.refund.refundMaximumValue != null) {
                            this.refundMaximumValue = response.data.refund.refundMaximumValue;
                        }
                        let cappedRefundValue = response.data.refund.amountResumeValue;
                        if (this.refundMaximumValue != null && cappedRefundValue > this.refundMaximumValue) {
                            cappedRefundValue = this.refundMaximumValue;
                        }
                        this.refundValue = cappedRefundValue;
                        this.refundObject.amountResumeValue = cappedRefundValue;
                    }
                    this.isLoadingCalculations = false;
                }).catch(() => {
                    this.isLoadingCalculations = false;
                })
            },
            addPayout(refund) {
                let payoutObject = {
                    tempRefundPayout: true,
                    currency: "€",
                    description: "Refund",
                    paymentDirection: 1,
                    treasurerUserAccount: this.account,
                    type: this.refundObject.paymentType,
                    value: this.refundValue,
                    refundId: refund.id,
                    refunds: [refund]
                }

                let tempPayout = this.getSelectedReservation.payments.find(payment => payment.tempRefundPayout == true);
                if (tempPayout) {
                    let paymentIndex = this.getSelectedReservation.payments.indexOf(tempPayout);
                    this.getSelectedReservation.payments[paymentIndex] = payoutObject;
                } else {
                    this.getSelectedReservation.payments.push(payoutObject);
                }
            },
            onAmountInput(e) {
                let value = e.target.value;

                if (this.isEuroMode) {
                    value = parseFloat(value);

                    if (!isNaN(value)) {
                        value = Math.floor(value * 100) / 100;
                    }
                } else {
                    value = parseInt(value);

                    if (!isNaN(value)) {
                        value = Math.max(0, Math.min(100, value));
                    }
                }

                this.refundObject.amount = isNaN(value) ? null : value;

                this.debouncedValueChanged();
            },
            preventDecimalInput(e) {
                if (!this.isEuroMode) {
                    if (e.key === '.' || e.key === ',') {
                        e.preventDefault();
                    }
                }
            },
            // Called by the parent wizard footer (Confirm button) via ref.
            async submit() {
                this.$v.$touch();

                if (this.isRefundValid == false) {
                    return false;
                }

                if (this.exceedsMaximumValue) {
                    Vue.toasted.error(`Refund value exceeds the maximum allowed amount of ${this.formatPrice(this.refundMaximumValue)} €.`);
                    return false;
                }

                // Refund requests stay pending and can still be canceled; only direct refunds are final
                if (!this.refundObject.isRequest) {
                    const confirmed = await this.$confirm('<h1>Confirm refund</h1><p class="mt-3">Are you sure you want to confirm this refund?<br><b>This action is irreversible!</b></p>');
                    if (!confirmed) return false;
                }

                this.isSubmitting = true;
                this.$set(this.refundObject, 'refundDetailsItems', this.refundObject.refundItems);

                try {
                    const payload = {
                        bookingNumber: this.getSelectedReservation.id,
                        transactionCode: this.getRefundTransactionCode,
                        refund: this.refundObject,
                    }

                    if (!this.getRefundTransactionCode) {
                        payload.paymentId = this.getRefundPaymentId;
                    }

                    const result = await refundService.createRefund(payload)

                    if (!result.ok) {
                        const beMessage = result.error && result.error.response && result.error.response.data && result.error.response.data.error;
                        Vue.toasted.error(beMessage || 'Something went wrong. Please try again.');
                        return
                    }

                    Vue.toasted.success('Refund created successfully.')
                    this.addPayout(result.value);
                    await this.refreshCurrentSelectedReservation();
                    this.eventHub.$emit('updateThisReservation', this.getSelectedReservation);
                    this.closeModal();
                } finally {
                    this.isSubmitting = false;
                }
            },
        },
        components: {
            Loader,
            paymentMethodSelector,
            vSelect: extendedVSelect,
            reduceQuantity,
            channelSelector,
            PaymentRow,
        }
    }
</script>

<style lang="scss" scoped>
    @import "~@/assets/scss/_utility.scss";
    @import "~@/assets/scss/_variables.scss";

    .refundTypeBtn {
        @extend .border-medium;
        height: 40px;
        border-radius: 0.4rem !important;

        &:hover {
            @extend .bg-lighter;
        }

        &.selected {
            border-color: $color-1 !important;
            background-color: rgba($color-1, 0.12) !important;
        }

        &.disabled,
        &:disabled {
            opacity: 0.5;
            background-color: $light;
            cursor: not-allowed !important;
        }
    }

    .whenToRefund {
        border: 1px solid $light;
        border-radius: 0.75rem;
        background: $lightest;
        overflow: hidden;

        &__option {
            & + & {
                border-top: 1px solid $light;
            }

            &:hover {
                background: $lighter;
            }
        }
    }

    .refund__all-summary {
        background: $lighter;
        border-radius: 0.375rem;
        padding: 0.75rem 1rem;
        font-weight: 600;
        color: $darkest;
    }

    .percentValue {
        &__label {
            font-size: 0.85rem;
        }

        &__input {
            width: 95px !important;
            font-size: 0.85rem;
        }

        &__select {
            width: 64px !important;

            ::v-deep .selected-tag {
                padding-right: 1.5rem;
            }
        }
    }

    .releaseQty {
        padding: 0.4rem 0.5rem !important;

        .mx-2 {
            transform: scale(0.8);
            transform-origin: left center;
        }

        &__title {
            font-size: 0.75rem;
        }

        &__subtitle {
            font-size: 0.65rem;
        }

        &__warning {
            font-size: 0.65rem;
            color: $orange;
            margin-top: 2px;
        }

        &--disabled {
            cursor: default !important;

            .releaseQty__title,
            .releaseQty__subtitle {
                opacity: 0.5;
            }
        }
    }
</style>



// WEBPACK FOOTER //
// Step2Details.vue?c9eb5730